import React, { useEffect, useRef, useState } from 'react';
import SeoHelmet from '../components/SeoHelmet';
import VideoHerosection from "../public/video-2-hero.mp4";

const About = () => {
  const [isVisible, setIsVisible] = useState(false);
  const [videoLoaded, setVideoLoaded] = useState(false);
  const [counters, setCounters] = useState({
    experience: 0,
    collaborations: 0,
    success: 0
  });
  
  const heroRef = useRef(null);
  const storyRef = useRef(null);
  const foundersRef = useRef(null);
  const servicesRef = useRef(null);
  const ctaRef = useRef(null);
  const videoRef = useRef(null);

  useEffect(() => {
    setIsVisible(true);

    const observerOptions = {
      threshold: 0.3,
      rootMargin: '0px 0px -50px 0px'
    };

    const observer = new IntersectionObserver((entries) => {
      entries.forEach((entry) => {
        if (entry.isIntersecting) {
          entry.target.classList.add('animate-fade-in-up');
        }
      });
    }, observerOptions);

    [heroRef, storyRef, foundersRef, servicesRef, ctaRef].forEach(ref => {
      if (ref.current) observer.observe(ref.current);
    });

    // Counter animation
    setTimeout(() => {
      const targetValues = { experience: 10, collaborations: 50, success: 100 };
      Object.keys(targetValues).forEach(key => {
        let currentValue = 0;
        const target = targetValues[key];
        const increment = target / 60;
        const timer = setInterval(() => {
          currentValue += increment;
          if (currentValue >= target) {
            setCounters(prev => ({ ...prev, [key]: target }));
            clearInterval(timer);
          } else {
            setCounters(prev => ({ ...prev, [key]: Math.floor(currentValue) }));
          }
        }, 30);
      });
    }, 1000);

    return () => observer.disconnect();
  }, []);

  const handleVideoLoad = () => {
    setVideoLoaded(true);
  };

  // Service Icons
  const serviceIcons = {
    'Distribution & Publishing': (
      <svg className="w-10 h-10" fill="none" stroke="currentColor" viewBox="0 0 24 24">
        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M19 11H5m14 0a2 2 0 012 2v6a2 2 0 01-2 2H5a2 2 0 01-2-2v-6a2 2 0 012-2m14 0V9a2 2 0 00-2-2M5 11V9a2 2 0 012-2m0 0V5a2 2 0 012-2h6a2 2 0 012 2v2M7 7h10" />
      </svg>
    ),
    'Marketing & Brand Partnerships': (
      <svg className="w-10 h-10" fill="none" stroke="currentColor" viewBox="0 0 24 24">
        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M11 5.882V19.24a1.76 1.76 0 01-3.417.592l-2.147-6.15M18 13a3 3 0 100-6M5.436 13.683A4.001 4.001 0 017 6h1.832c4.1 0 7.625-1.234 9.168-3v14c-1.543-1.766-5.067-3-9.168-3H7a3.988 3.988 0 01-1.564-.317z" />
      </svg>
    ),
    'Sync Placements': (
      <svg className="w-10 h-10" fill="none" stroke="currentColor" viewBox="0 0 24 24">
        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M9 19V6l12-3v13M9 19c0 1.105-1.343 2-3 2s-3-.895-3-2 1.343-2 3-2 3 .895 3 2zm12-3c0 1.105-1.343 2-3 2s-3-.895-3-2 1.343-2 3-2 3 .895 3 2zM9 10l12-3" />
      </svg>
    ),
    'Bookings & Touring': (
      <svg className="w-10 h-10" fill="none" stroke="currentColor" viewBox="0 0 24 24">
        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M8 7V3m8 4V3m-9 8h10M5 21h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v12a2 2 0 002 2z" />
      </svg>
    ),
    'Music Advisory': (
      <svg className="w-10 h-10" fill="none" stroke="currentColor" viewBox="0 0 24 24">
        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M9 12l2 2 4-4m5.618-4.016A11.955 11.955 0 0112 2.944a11.955 11.955 0 01-8.618 3.04A12.02 12.02 0 003 9c0 5.591 3.824 10.29 9 11.622 5.176-1.332 9-6.03 9-11.622 0-1.042-.133-2.052-.382-3.016z" />
      </svg>
    ),
    'Artist Development': (
      <svg className="w-10 h-10" fill="none" stroke="currentColor" viewBox="0 0 24 24">
        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M12 4.354a4 4 0 110 5.292M15 21H3v-1a6 6 0 0112 0v1zm0 0h6v-1a6 6 0 00-9-5.197m13.5-9a2.5 2.5 0 11-5 0 2.5 2.5 0 015 0z" />
      </svg>
    )
  };

  const services = [
    'Distribution & Publishing',
    'Marketing & Brand Partnerships', 
    'Sync Placements',
    'Bookings & Touring',
    'Music Advisory',
    'Artist Development'
  ];

  return (
    <>
      <SeoHelmet
        title="About Hi-Way 89 Entertainment | Building Bridges in Music"
        description="We didn't start Hi-Way 89 Entertainment to fix a broken system – we started it to build a new one. We're building a bridge to close the gap between the Canadian and African music industries."
        canonical="/about"
        keywords="Hi-Way 89, music bridge, Canadian music, African music, cross-continental collaboration, artist development, music infrastructure, Ikenna Nwagboso, Camillo Doregos"
        ogType="website"
        ogImage="/og-about.jpg"
      />
      
      <div className="min-h-screen bg-black text-white">
        {/* Hero Section with Video */}
        <section ref={heroRef} className="relative min-h-screen flex items-center justify-center px-4 md:px-6 overflow-hidden">
          {/* Background Video */}
          <div className="absolute inset-0">
            <video
              ref={videoRef}
              autoPlay
              muted
              loop
              playsInline
              className="w-full h-full object-cover"
              style={{ 
                filter: 'brightness(0.4) contrast(1.1)',
                opacity: videoLoaded ? 1 : 0,
                transition: 'opacity 1s ease-in'
              }}
              onLoadedData={handleVideoLoad}
            >
              <source src={VideoHerosection} type="video/mp4" />
              <div className="absolute inset-0 bg-gradient-to-br from-purple-900 to-black"></div>
            </video>
            
            {!videoLoaded && (
              <div className="absolute inset-0 bg-gradient-to-br from-purple-900/20 to-black/80"></div>
            )}
            
            <div className="absolute inset-0 bg-gradient-to-b from-black/70 via-transparent to-black/80"></div>
            <div className="absolute inset-0 bg-gradient-to-r from-black/40 to-purple-900/20"></div>
          </div>
          
          {/* Animated background pattern */}
          <div className="absolute inset-0 opacity-20">
            <div className="absolute top-20 left-20 w-32 h-32 border border-white/30 rounded-full animate-pulse"></div>
            <div className="absolute bottom-40 right-32 w-24 h-24 border border-purple-500/30 rounded-full animate-pulse delay-1000"></div>
            <div className="absolute top-1/2 left-1/3 w-16 h-16 border border-white/20 rounded-full animate-pulse delay-500"></div>
          </div>

          <div className="container mx-auto max-w-6xl relative z-10 text-center">
            <div className={`transition-all duration-1500 ${isVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-12'}`}>
              <h1 className="text-5xl sm:text-6xl md:text-8xl lg:text-9xl font-black mb-8 tracking-tighter bg-gradient-to-r from-white to-gray-300 bg-clip-text text-transparent">
                ABOUT
              </h1>
              <div className="w-32 h-1 bg-white mx-auto mb-8"></div>
              <p className="text-xl sm:text-2xl md:text-3xl text-gray-300 max-w-4xl mx-auto leading-relaxed font-light">
                We didn't start Hi-Way 89 Entertainment to fix a broken system – we started it to build a new one.
              </p>
            </div>
          </div>
        </section>

        {/* Our Story Section */}
        <section ref={storyRef} className="py-20 px-4 md:px-6 bg-white text-black opacity-0">
          <div className="container mx-auto max-w-6xl">
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-16 items-start">
              <div>
                <h2 className="text-4xl md:text-6xl font-black mb-6 tracking-tight">
                  BUILDING BRIDGES
                </h2>
                <div className="w-20 h-1 bg-black mb-8"></div>
                
                <div className="space-y-6 text-lg leading-relaxed">
                  <p>
                    We're building a bridge to close the gap between the Canadian and African music industries and connect both to the rest of the world.
                  </p>
                  
                  <p>
                    After spending years working with some of the best talent across Africa and Canada, we noticed a disconnect that couldn't be ignored.
                  </p>
                  
                  <p>
                    Black music dominates Canadian streaming charts, contributing the majority of streaming revenue, and Afrobeats, which has exploded globally, influences Canadian artists and fans. But that cultural exchange has been largely one-sided.
                  </p>
                  
                  <p>
                    Canadian artists are ready to explore African and global markets, while African artists are eager to connect with North American audiences, but the bridge between them hasn't been easy to find.
                  </p>
                  
                  <p className="font-bold text-2xl">
                    And this is where Hi-Way 89 comes in.
                  </p>
                </div>
              </div>
              
              <div className="space-y-8">
                <div className="bg-gray-100 p-8 rounded-2xl transform hover:scale-105 transition-all duration-300">
                  <h3 className="text-2xl font-black mb-4">OUR VISION</h3>
                  <p className="text-lg leading-relaxed">
                    We're not just about signing artists; we're building the much-needed infrastructure that Canadian artists have always needed but never had. We're connecting them with award-winning producers, establishing powerful cross-continental collaborations, and creating the opportunities that turn local talent into global stars.
                  </p>
                </div>
                
                <div className="bg-black text-white p-8 rounded-2xl transform hover:scale-105 transition-all duration-300">
                  <h3 className="text-2xl font-black mb-4">THE FUTURE</h3>
                  <p className="text-lg leading-relaxed">
                    Imagine a world where Canadian and African artists collaborate seamlessly, tour both continents, and make global hits together.
                  </p>
                  <p className="text-xl font-bold mt-4">
                    We're making that happen.
                  </p>
                </div>
                
             
              </div>
            </div>
          </div>
        </section>

        {/* Services Section */}
        <section ref={servicesRef} className="py-20 px-4 md:px-6 bg-white text-black opacity-0">
          <div className="container mx-auto max-w-6xl">
            <div className="text-center mb-16">
              <h2 className="text-4xl md:text-6xl font-black mb-6 tracking-tight">
                WHAT WE OFFER
              </h2>
              <div className="w-20 h-1 bg-black mx-auto mb-8"></div>
              <p className="text-xl text-gray-700 max-w-3xl mx-auto">
                Hi-Way 89 offers:
              </p>
            </div>
            
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
              {services.map((service, index) => (
                <div 
                  key={index}
                  className="group p-8 bg-gray-100 rounded-2xl hover:bg-black hover:text-white transition-all duration-500 transform hover:scale-105 cursor-pointer"
                >
                  <div className="text-center">
                    <div className="mb-6 flex justify-center">
                      <div className="w-16 h-16 bg-black group-hover:bg-white rounded-full flex items-center justify-center transition-colors duration-300">
                        <div className="text-white group-hover:text-black transition-colors duration-300">
                          {serviceIcons[service]}
                        </div>
                      </div>
                    </div>
                    <h3 className="text-xl font-black mb-4 tracking-wide">
                      {service.toUpperCase()}
                    </h3>
                  </div>
                </div>
              ))}
            </div>
            
          
          </div>
        </section>

        {/* CTA Section */}
        <section ref={ctaRef} className="py-20 px-4 md:px-6 bg-black opacity-0">
          <div className="container mx-auto max-w-4xl text-center">
            <h2 className="text-4xl md:text-6xl font-black mb-8 tracking-tight">
              JOIN THE MOVEMENT
            </h2>
            <p className="text-xl text-gray-300 mb-12 max-w-2xl mx-auto">
              Ready to be part of the bridge that connects continents and transforms the music industry?
            </p>
            
            <div className="flex flex-col sm:flex-row gap-6 justify-center">

            
            </div>
          </div>
        </section>

        {/* CSS for animations */}
        <style jsx>{`
          .animate-fade-in-up {
            animation: fadeInUp 1s ease-out forwards;
          }
          
          @keyframes fadeInUp {
            from {
              opacity: 0;
              transform: translateY(30px);
            }
            to {
              opacity: 1;
              transform: translateY(0);
            }
          }
        `}</style>
      </div>
    </>
  );
};

export default About;